import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    Chip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    TextField,
    IconButton,
    Tooltip,
    Alert,
    Collapse,
    CircularProgress,
    Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { printThermalReceipt } from '../utils/printHelper';

export default function OfflineQueueModal({ open, onClose }) {
    const { 
        offlineOrdersQueue, 
        syncOfflineOrders, 
        isOnline, 
        branches, 
        hotelName, 
        currentUser 
    } = useApp();
    const { currentLanguage } = useLanguage();
    const isMr = currentLanguage === 'mr';
    const isHi = currentLanguage === 'hi';

    const [isSyncing, setIsSyncing] = useState(false);
    const [expandedOrderId, setExpandedOrderId] = useState(null);

    // Column Filter States (Matching ReportsView grid style)
    const [filterOrderId, setFilterOrderId] = useState('');
    const [filterTime, setFilterTime] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterWaiter, setFilterWaiter] = useState('');
    const [filterPayment, setFilterPayment] = useState('');

    const queueList = Array.isArray(offlineOrdersQueue) ? offlineOrdersQueue : [];

    const getBranchName = (branchId) => {
        if (!branchId) return currentUser?.branch_name || currentUser?.tenant_name || hotelName || 'Main Branch';
        const found = (branches || []).find(b => String(b.id) === String(branchId));
        return found ? (found.name || found.branch_name) : (currentUser?.branch_name || hotelName || 'Main Branch');
    };

    const formatDateDDMMYYYY = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return String(dateStr);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}-${month}-${year}`;
        } catch (e) {
            return String(dateStr);
        }
    };

    const formatTimeAMPM = (dateStr) => {
        if (!dateStr) return '';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return '';
            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (e) {
            return '';
        }
    };

    const handleSyncClick = async () => {
        setIsSyncing(true);
        try {
            await syncOfflineOrders(false);
        } finally {
            setIsSyncing(false);
        }
    };

    const handlePrintOrder = (order) => {
        const branchObj = (branches || []).find(b => String(b.id) === String(order.branch_id));
        const targetHotelName = branchObj?.name || branchObj?.branch_name || getBranchName(order.branch_id);
        const orderToPrint = {
            ...order,
            id: order.id || order.order_id,
            order_id: order.id || order.order_id,
            hotelName: targetHotelName,
            branch_name: targetHotelName,
            branch_address: branchObj?.address || branchObj?.branch_address,
            branch_phone: branchObj?.phone || branchObj?.branch_phone,
            upi_id: branchObj?.upi_id || branchObj?.upiId,
            items: Array.isArray(order.items) ? order.items.map(it => ({
                id: it.menu_item_id || it.id,
                name: it.name || it.item_name || 'Item',
                quantity: it.quantity || 1,
                price: it.price || 0,
                remarks: it.remarks || it.notes || ''
            })) : []
        };
        printThermalReceipt(orderToPrint, targetHotelName);
    };

    // Filtered Queue List
    const filteredQueue = queueList.filter(o => {
        const orderId = String(o.id || o.order_id || '').toLowerCase();
        const timeStr = ((o.created_at || o.createdAt || o.timestamp) ? `${formatDateDDMMYYYY(o.created_at || o.createdAt || o.timestamp)} ${formatTimeAMPM(o.created_at || o.createdAt || o.timestamp)}` : '').toLowerCase();
        const typeStr = String(o.type || o.order_source || o.orderSource || '').toLowerCase();
        const waiterStr = String(o.waiter_name || o.waiterName || '').toLowerCase();
        const paymentStr = String(o.payment_method || o.paymentMethod || o.status || '').toLowerCase();

        if (filterOrderId && !orderId.includes(filterOrderId.toLowerCase().trim())) return false;
        if (filterTime && !timeStr.includes(filterTime.toLowerCase().trim())) return false;
        if (filterType && !typeStr.includes(filterType.toLowerCase().trim())) return false;
        if (filterWaiter && !waiterStr.includes(filterWaiter.toLowerCase().trim())) return false;
        if (filterPayment && !paymentStr.includes(filterPayment.toLowerCase().trim())) return false;

        return true;
    });

    const totalQueueAmount = queueList.reduce((sum, o) => sum + (Number(o.total) || 0), 0);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    overflow: 'hidden'
                }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{
                bgcolor: '#0f172a',
                color: '#ffffff',
                p: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        bgcolor: queueList.length > 0 ? '#f59e0b' : '#10b981',
                        color: '#ffffff',
                        p: 1,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {queueList.length > 0 ? <WifiOffIcon fontSize="small" /> : <CloudDoneIcon fontSize="small" />}
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 900, fontSize: { xs: '1rem', sm: '1.15rem' }, lineHeight: 1.2 }}>
                            {isMr ? '📦 ऑफलाईन ऑर्डर्स Queue विवरण' : (isHi ? '📦 ऑफलाइन ऑर्डर्स Queue विवरण' : '📦 Offline Orders Queue Details')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                            {isMr 
                                ? `एकूण ${queueList.length} प्रलंबित ऑर्डर्स (एकूण रक्कम: ₹${totalQueueAmount.toFixed(0)})` 
                                : `Total ${queueList.length} Pending Orders (Total Amount: ₹${totalQueueAmount.toFixed(0)})`}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                        label={isOnline ? '🟢 Online' : '🔴 Offline'} 
                        size="small" 
                        sx={{ 
                            bgcolor: isOnline ? '#065f46' : '#991b1b', 
                            color: '#ffffff', 
                            fontWeight: 800,
                            fontSize: '0.75rem' 
                        }} 
                    />
                    <IconButton onClick={onClose} sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f8fafc' }}>
                {/* Info Notice Banner */}
                <Alert 
                    severity={queueList.length > 0 ? "warning" : "success"}
                    sx={{ 
                        mb: 2.5, 
                        borderRadius: 2.5, 
                        fontWeight: 700,
                        border: `1px solid ${queueList.length > 0 ? '#fde68a' : '#a7f3d0'}`,
                        bgcolor: queueList.length > 0 ? '#fffbeb' : '#f0fdf4',
                        color: queueList.length > 0 ? '#92400e' : '#065f46'
                    }}
                >
                    {queueList.length > 0 ? (
                        isMr 
                            ? `ℹ️ इंटरनेट बंद असताना किंवा सर्व्हर कनेक्शन तात्पुरते खंडित असताना या ${queueList.length} ऑर्डर्स तुमच्या कॉम्प्युटर/टॅबलेटच्या मेमरीमध्ये सुरक्षित सेव्ह झालेल्या आहेत. इंटरनेट सुरू झाल्यावर 'आता सर्व ऑर्डर्स ऑनलाईन सिंक करा' बटनावर क्लिक करा.`
                            : `ℹ️ These ${queueList.length} order(s) are stored safely in local memory because of temporary network disconnect. Click 'Sync All to Cloud Now' when online.`
                    ) : (
                        isMr
                            ? `✅ सर्व ऑर्डर्स ऑनलाईन क्लाऊड डेटाबेसवर यशस्वीरीत्या सिंक झालेल्या आहेत! सध्या कोणतीही ऑर्डर ऑफलाईन क्यू मध्ये प्रलंबित नाही.`
                            : `✅ All orders are successfully synced with the cloud database! Offline queue is currently empty.`
                    )}
                </Alert>

                {queueList.length === 0 ? (
                    <Box sx={{ 
                        py: 6, 
                        textAlign: 'center', 
                        bgcolor: '#ffffff', 
                        borderRadius: 3, 
                        border: '1px dashed #cbd5e1' 
                    }}>
                        <CloudDoneIcon sx={{ fontSize: 60, color: '#10b981', mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
                            {isMr ? 'कोणतीही ऑफलाईन ऑर्डर प्रलंबित नाही' : 'No Offline Orders Pending'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', maxWidth: 480, mx: 'auto' }}>
                            {isMr 
                                ? 'तुमचा सर्व POS व KOT डेटा थेट मुख्य डेटाबेसवर अद्ययावत (Up-to-date) आहे.' 
                                : 'All your POS & KOT transactions are fully updated on the live database.'}
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, overflow: 'hidden' }}>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#ffffff' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 900, width: 70, textAlign: 'center', color: '#0f172a' }}>
                                        {isMr ? 'बिल #' : 'Sr. No.'}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 900, color: '#0f172a' }}>
                                        {isMr ? 'ऑर्डर ID' : 'Order ID'}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 900, color: '#0f172a' }}>
                                        {isMr ? 'शाखा' : 'Branch'}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 900, color: '#0f172a' }}>
                                        {isMr ? 'वेळ / दिनांक' : 'Time'}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 900, color: '#0f172a' }}>
                                        {isMr ? 'प्रकार' : 'Type'}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 900, color: '#0f172a' }}>
                                        {isMr ? 'वेटर / ऑपरेटर' : 'Waiter'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                        {isMr ? 'उपएकूण' : 'Subtotal'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                        {isMr ? 'सूट (₹)' : 'Discounts'}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                        {isMr ? 'एकूण रक्कम' : 'Grand Total'}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 900, color: '#0f172a' }}>
                                        {isMr ? 'पेमेंट / स्थिती' : 'Payment Mode'}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                        {isMr ? 'कृती / पावती' : 'Actions'}
                                    </TableCell>
                                </TableRow>

                                {/* Column Filter Search Row (Exact match to screenshot) */}
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    <TableCell sx={{ p: 0.8 }}></TableCell>
                                    <TableCell sx={{ p: 0.8 }}>
                                        <TextField 
                                            size="small" 
                                            placeholder="Search..." 
                                            variant="outlined" 
                                            value={filterOrderId} 
                                            onChange={(e) => setFilterOrderId(e.target.value)} 
                                            sx={{ bgcolor: '#ffffff', '& input': { py: 0.6, fontSize: '0.75rem' } }} 
                                        />
                                    </TableCell>
                                    <TableCell sx={{ p: 0.8 }}></TableCell>
                                    <TableCell sx={{ p: 0.8 }}>
                                        <TextField 
                                            size="small" 
                                            placeholder="Search..." 
                                            variant="outlined" 
                                            value={filterTime} 
                                            onChange={(e) => setFilterTime(e.target.value)} 
                                            sx={{ bgcolor: '#ffffff', '& input': { py: 0.6, fontSize: '0.75rem' } }} 
                                        />
                                    </TableCell>
                                    <TableCell sx={{ p: 0.8 }}>
                                        <TextField 
                                            size="small" 
                                            placeholder="Search..." 
                                            variant="outlined" 
                                            value={filterType} 
                                            onChange={(e) => setFilterType(e.target.value)} 
                                            sx={{ bgcolor: '#ffffff', '& input': { py: 0.6, fontSize: '0.75rem' } }} 
                                        />
                                    </TableCell>
                                    <TableCell sx={{ p: 0.8 }}>
                                        <TextField 
                                            size="small" 
                                            placeholder="Search..." 
                                            variant="outlined" 
                                            value={filterWaiter} 
                                            onChange={(e) => setFilterWaiter(e.target.value)} 
                                            sx={{ bgcolor: '#ffffff', '& input': { py: 0.6, fontSize: '0.75rem' } }} 
                                        />
                                    </TableCell>
                                    <TableCell sx={{ p: 0.8 }}></TableCell>
                                    <TableCell sx={{ p: 0.8 }}></TableCell>
                                    <TableCell sx={{ p: 0.8 }}></TableCell>
                                    <TableCell sx={{ p: 0.8 }}>
                                        <TextField 
                                            size="small" 
                                            placeholder="Search..." 
                                            variant="outlined" 
                                            value={filterPayment} 
                                            onChange={(e) => setFilterPayment(e.target.value)} 
                                            sx={{ bgcolor: '#ffffff', '& input': { py: 0.6, fontSize: '0.75rem' } }} 
                                        />
                                    </TableCell>
                                    <TableCell sx={{ p: 0.8 }}></TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredQueue.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center" sx={{ py: 3, color: '#64748b', fontWeight: 600 }}>
                                            {isMr ? 'शोध परिणामात कोणतीही ऑर्डर सापडली नाही' : 'No matching orders found in offline queue'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredQueue.map((o, index) => {
                                        const ordId = o.id || o.order_id || `OFF-${index + 1}`;
                                        const isExpanded = expandedOrderId === ordId;
                                        const itemsList = Array.isArray(o.items) ? o.items : [];
                                        const dt = o.created_at || o.createdAt || o.timestamp || new Date().toISOString();
                                        const isSettled = o.status === 'served' || Boolean(o.payment_method || o.paymentMethod);

                                        return (
                                            <React.Fragment key={ordId}>
                                                <TableRow hover sx={{ '&:hover': { bgcolor: '#f1f5f9' }, bgcolor: '#ffffff' }}>
                                                    {/* Daily Bill # / Sr. No. */}
                                                    <TableCell align="center">
                                                        <Chip 
                                                            label={`#${index + 1}`} 
                                                            size="small" 
                                                            sx={{ 
                                                                fontWeight: 900, 
                                                                bgcolor: '#f1f5f9', 
                                                                color: '#0f172a', 
                                                                border: '1px solid #cbd5e1',
                                                                height: 24,
                                                                fontSize: '0.75rem'
                                                            }} 
                                                        />
                                                    </TableCell>

                                                    {/* Order ID */}
                                                    <TableCell sx={{ fontWeight: 900, color: '#0f172a' }}>
                                                        {ordId}
                                                    </TableCell>

                                                    {/* Branch */}
                                                    <TableCell>
                                                        <Chip
                                                            label={getBranchName(o.branch_id)}
                                                            size="small"
                                                            sx={{ 
                                                                fontWeight: 800, 
                                                                bgcolor: '#eff6ff', 
                                                                color: '#1d4ed8', 
                                                                border: '1px solid #bfdbfe',
                                                                fontSize: '0.72rem',
                                                                height: 24
                                                            }}
                                                        />
                                                    </TableCell>

                                                    {/* Time */}
                                                    <TableCell sx={{ fontSize: '0.75rem', color: '#475569', whiteSpace: 'nowrap' }}>
                                                        <Box sx={{ fontWeight: 700 }}>{formatDateDDMMYYYY(dt)}</Box>
                                                        <Box sx={{ color: '#64748b' }}>{formatTimeAMPM(dt)}</Box>
                                                    </TableCell>

                                                    {/* Type */}
                                                    <TableCell>
                                                        <Chip 
                                                            label={o.type || (o.table_id === 'counter' ? 'Counter Parcel' : `Table ${o.table_id || ''}`)} 
                                                            size="small" 
                                                            sx={{ 
                                                                fontWeight: 800, 
                                                                fontSize: '0.72rem', 
                                                                height: 24,
                                                                bgcolor: String(o.type || '').toLowerCase().includes('zomato') ? '#fef2f2' : (String(o.type || '').toLowerCase().includes('swiggy') ? '#fff7ed' : '#f8fafc'),
                                                                color: String(o.type || '').toLowerCase().includes('zomato') ? '#dc2626' : (String(o.type || '').toLowerCase().includes('swiggy') ? '#ea580c' : '#334155'),
                                                                border: '1px solid #e2e8f0'
                                                            }} 
                                                        />
                                                    </TableCell>

                                                    {/* Waiter */}
                                                    <TableCell sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.8rem' }}>
                                                        {o.waiter_name || o.waiterName || 'Staff'}
                                                    </TableCell>

                                                    {/* Subtotal */}
                                                    <TableCell align="right" sx={{ fontWeight: 700, color: '#334155', fontSize: '0.85rem' }}>
                                                        ₹{Number(o.subtotal || o.total || 0).toFixed(0)}
                                                    </TableCell>

                                                    {/* Discounts */}
                                                    <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700, fontSize: '0.85rem' }}>
                                                        -₹{Number(o.discount || 0).toFixed(0)}
                                                    </TableCell>

                                                    {/* Grand Total */}
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#008751', fontSize: '0.95rem' }}>
                                                        ₹{Number(o.total || o.subtotal || 0).toFixed(0)}
                                                    </TableCell>

                                                    {/* Payment Mode / Status */}
                                                    <TableCell>
                                                        {isSettled ? (
                                                            <Chip 
                                                                label={o.payment_method || o.paymentMethod || 'cash'} 
                                                                size="small" 
                                                                color="success" 
                                                                variant="outlined" 
                                                                sx={{ fontWeight: 800, height: 24, fontSize: '0.72rem', textTransform: 'lowercase' }} 
                                                            />
                                                        ) : (
                                                            <Chip
                                                                label={isMr ? '⚠️ Cooking / Unsettled' : '⚠️ Unsettled'}
                                                                size="small"
                                                                sx={{ 
                                                                    fontWeight: 800, 
                                                                    bgcolor: '#fef3c7', 
                                                                    color: '#b45309', 
                                                                    border: '1px solid #fde68a',
                                                                    height: 24,
                                                                    fontSize: '0.72rem'
                                                                }}
                                                            />
                                                        )}
                                                    </TableCell>

                                                    {/* Actions (Print & Expand details ONLY - NO Delete, NO Edit) */}
                                                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                                        <Box sx={{ display: 'flex', gap: 0.8, justifyContent: 'center', alignItems: 'center' }}>
                                                            {/* Print Thermal Bill Button */}
                                                            <Tooltip title={isMr ? "पावती प्रिंट करा (Print Bill)" : "Print Bill"}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handlePrintOrder(o)}
                                                                    sx={{
                                                                        bgcolor: '#008751',
                                                                        color: '#ffffff',
                                                                        width: 32,
                                                                        height: 32,
                                                                        borderRadius: 2,
                                                                        '&:hover': { bgcolor: '#006f42' }
                                                                    }}
                                                                >
                                                                    <PrintIcon sx={{ fontSize: 18 }} />
                                                                </IconButton>
                                                            </Tooltip>

                                                            {/* View Items Toggle Button */}
                                                            <Tooltip title={isExpanded ? (isMr ? "तपशील लपवा" : "Hide Items") : (isMr ? "पदार्थ पहा" : "View Items")}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => setExpandedOrderId(isExpanded ? null : ordId)}
                                                                    sx={{
                                                                        bgcolor: isExpanded ? '#e2e8f0' : '#f8fafc',
                                                                        color: '#475569',
                                                                        width: 32,
                                                                        height: 32,
                                                                        borderRadius: 2,
                                                                        border: '1px solid #cbd5e1',
                                                                        '&:hover': { bgcolor: '#e2e8f0' }
                                                                    }}
                                                                >
                                                                    {isExpanded ? <KeyboardArrowUpIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>

                                                {/* Expanded Items Breakdown Accordion */}
                                                <TableRow>
                                                    <TableCell colSpan={11} sx={{ p: 0, borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none' }}>
                                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderLeft: '4px solid #008751', ml: 2, my: 1, borderRadius: 2 }}>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <RestaurantMenuIcon sx={{ fontSize: 18, color: '#008751' }} />
                                                                    {isMr ? 'ऑर्डरमधील पदार्थ (Ordered Items):' : 'Ordered Items in this bill:'}
                                                                </Typography>
                                                                
                                                                {itemsList.length === 0 ? (
                                                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                                        {isMr ? 'या ऑर्डरमधील पदार्थांचे तपशील उपलब्ध नाहीत.' : 'No item breakdown details available.'}
                                                                    </Typography>
                                                                ) : (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                        {itemsList.map((item, itIdx) => (
                                                                            <Chip
                                                                                key={itIdx}
                                                                                label={`${item.quantity}x ${item.name || item.item_name || 'Dish'} @ ₹${item.price || 0}${item.remarks ? ` (${item.remarks})` : ''}`}
                                                                                sx={{
                                                                                    bgcolor: '#ffffff',
                                                                                    border: '1px solid #cbd5e1',
                                                                                    fontWeight: 700,
                                                                                    color: '#1e293b',
                                                                                    fontSize: '0.8rem'
                                                                                }}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            {/* Footer Actions */}
            <DialogActions sx={{ p: 2.5, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
                        {isMr ? 'सुरक्षा सूचना: ऑपरेटरला ऑफलाईन ऑर्डर्स डिलीट किंवा बदल करण्याची परवानगी नाही.' : 'Security Notice: Operators cannot delete or modify offline queue orders.'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        sx={{
                            fontWeight: 800,
                            borderRadius: 2,
                            textTransform: 'none',
                            color: '#475569',
                            borderColor: '#cbd5e1'
                        }}
                    >
                        {isMr ? 'बंद करा' : (isHi ? 'बंद करें' : 'Close')}
                    </Button>

                    {queueList.length > 0 && (
                        <Button
                            variant="contained"
                            color="success"
                            disabled={isSyncing}
                            onClick={handleSyncClick}
                            startIcon={isSyncing ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                            sx={{
                                fontWeight: 900,
                                borderRadius: 2,
                                textTransform: 'none',
                                bgcolor: '#008751',
                                '&:hover': { bgcolor: '#006f42' },
                                px: 2.5
                            }}
                        >
                            {isSyncing 
                                ? (isMr ? 'सिंक होत आहे...' : 'Syncing...') 
                                : (isMr ? '⚡ आता सर्व ऑर्डर्स ऑनलाईन सिंक करा' : (isHi ? '⚡ अभी सभी ऑर्डर्स ऑनलाइन सिंक करें' : '⚡ Sync All to Cloud Now'))}
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
}
